import { useEffect, useRef, useState } from 'react'

/**
 * Move focus to a keyed element after the next render: the row a user returns to after closing an editor, the
 * neighbour after a delete, the restored row after Undo. Elements register with `register(key)` as a ref callback.
 */
export const useFocusRequest = () => {
  const elements = useRef(new Map<string, HTMLElement>())
  const pending = useRef<string | null>(null)
  const [, setTick] = useState(0)

  useEffect(() => {
    const key = pending.current
    if (key) {
      pending.current = null
      elements.current.get(key)?.focus()
    }
  })

  const register = (key: string) => (element: HTMLElement | null) => {
    if (element) {
      elements.current.set(key, element)
      return
    }
    elements.current.delete(key)
  }

  const request = (key: string) => {
    pending.current = key
    setTick(tick => tick + 1)
  }

  return { register, request }
}

export type FocusRequest = ReturnType<typeof useFocusRequest>
